import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { CALENDAR_EVENTS_QUERY_KEY } from '@/app/(main)/calendar/consts/query-keys';
import { getCalendarItemsUrl } from '@/app/(main)/calendar/utils/getCalendarItemsUrl';
import {
  mapCalendarApiItemsToEvents,
  parseCalendarItemsResponse,
} from '@/app/(main)/calendar/utils/mapCalendarApiItems';
import { mapCalendarVisibilityToApiEventTypes } from '@/app/(main)/calendar/utils/mapCalendarVisibilityToApiEventTypes';
import { useFetch } from '@/hooks/useFetch';
import type { GetCalendarItemsResponse } from '@/types/calendarApiTypes';
import type { GetCalendarEventsParams } from '@/types/calendarTypes';

type UseGetCalendarEventsQueryOptions = {
  enabled?: boolean;
};

export function useGetCalendarEventsQuery(
  params: GetCalendarEventsParams,
  options: UseGetCalendarEventsQueryOptions = {}
) {
  const { doFetch } = useFetch<void, GetCalendarItemsResponse>();
  const eventTypes = mapCalendarVisibilityToApiEventTypes(params.visibility);

  return useQuery({
    queryKey: [CALENDAR_EVENTS_QUERY_KEY, params.date, params.view, params.visibility],
    enabled: (options.enabled ?? true) || Boolean(params.date) || eventTypes.length > 0,
    queryFn: async () => {
      const response = await doFetch(
        getCalendarItemsUrl({
          date: params.date,
          view: params.view,
          eventTypes,
        }),
        {
          suppressErrorToast: true,
        }
      );
      return mapCalendarApiItemsToEvents(parseCalendarItemsResponse(response));
    },
    placeholderData: keepPreviousData,
  });
}
